import { FunctionsFetchError } from '@supabase/supabase-js';

import { CorrespondenceApiError } from '@/lib/errors/correspondence-api-error';
import {
  FUNCTIONS_FETCH_ERROR_MESSAGE,
  POSTGREST_TRANSPORT_FAILURE_MESSAGES,
} from '@/lib/errors/transport-error-signature';

/**
 * Transport-level invoke failures: the request never produced an HTTP response.
 * supabase-js throws `FunctionsFetchError` when the underlying `fetch` rejects
 * (network reset, CORS, cold-start) or — most often in practice — when the
 * request is aborted because the caller navigated away mid-flight. These are not
 * actionable application errors. Callers SHOULD still surface them to the query
 * error state, but MUST NOT log them at `console.error` — Sentry's console
 * integration captures that as an error and a transient blip becomes noise.
 */
export function isTransientFetchError(error: unknown): boolean {
  if (error instanceof FunctionsFetchError) {
    return true;
  }
  // invokeCorrespondenceApi wraps a transport failure (the underlying
  // FunctionsFetchError — the request never produced an HTTP response) into a
  // CorrespondenceApiError with httpStatus 0, carrying the SDK's fetch-error
  // message. Match that signature specifically — NOT every httpStatus-0 error:
  // the "empty response" contract failure (unexpected 2xx + null body) is also
  // httpStatus 0 but is a real bug that must stay visible in Sentry. Kept in sync
  // with the beforeSend backstop in sentry.ts, which drops the same string.
  if (
    error instanceof CorrespondenceApiError &&
    error.httpStatus === 0 &&
    error.message.includes(FUNCTIONS_FETCH_ERROR_MESSAGE)
  ) {
    return true;
  }
  // A PostgREST transport failure surfaces as a PostgrestError whose message is
  // the stringified fetch rejection — see POSTGREST_TRANSPORT_FAILURE_MESSAGES.
  const message = (error as { message?: unknown } | null)?.message;
  if (
    typeof message === 'string' &&
    POSTGREST_TRANSPORT_FAILURE_MESSAGES.some((text) => message.includes(text))
  ) {
    return true;
  }
  // An aborted fetch rejects with a `DOMException` named 'AbortError', which is
  // not an `Error` subclass in every runtime — match on the name structurally.
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}
